/* RESPONSIBLE TEAM: team-automation-foundations */
/* eslint-disable no-restricted-imports */
import {
  TRIGGERABLE_BOT_TYPE,
  FIN_CLOSED_CONVERSATION_TARGET,
  FIN_CLOSED_CONVERSATION_TRIGGER,
  DURING_CONVERSATION,
  WORKFLOWS_SETUP_TRIGGER_DURING_CONVERSATION,
  WORKFLOWS_SETUP_TRIGGER_INTERNAL_PROCESS,
} from 'embercom/lib/operator/custom-bots/constants';
import {
  matchBehaviors,
  objectTypes,
} from 'embercom/models/data/matching-system/matching-constants';
import FinClosedConversationEditorConfig from 'embercom/objects/visual-builder/configuration/editor/fin-closed-conversation';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

import CustomBotConfig, { type ColumnConfigs, type TriggerPredicates } from '../custom-bot';
import type { BotConfigTarget } from '../configuration';
import { getApp } from 'embercom/lib/container-lookup';

export default class FinClosedConversationCustomBotConfig extends CustomBotConfig {
  get target(): BotConfigTarget {
    return FIN_CLOSED_CONVERSATION_TARGET;
  }

  get botType() {
    return TRIGGERABLE_BOT_TYPE;
  }

  get objectType() {
    return objectTypes.triggerableCustomBot;
  }

  get trigger() {
    return FIN_CLOSED_CONVERSATION_TRIGGER;
  }

  get matchBehavior() {
    return matchBehaviors.transient;
  }

  get titleTranslationKey() {
    return 'operator.custom-bot.trigger.fin-closed-conversation.title';
  }

  get descriptionTranslationKey() {
    return 'operator.custom-bot.trigger.fin-closed-conversation.description';
  }

  get icon(): InterfaceIconName {
    return 'fin';
  }

  get editorConfig() {
    return new this.EditorConfigClass({
      targetChannels: this.customBot?.targetChannels,
      areStepPaywallsActive: this.areStepPaywallsActive,
      workflow: this.workflow,
    });
  }

  get EditorConfigClass() {
    return FinClosedConversationEditorConfig;
  }

  get triggerPredicates(): TriggerPredicates {
    return {
      type: 'or',
      options: [
        {
          icon: 'fin',
          iconSet: 'standard',
          value: 'confirmed_resolution',
          translationKey:
            'operator.custom-bot.trigger.fin-closed-conversation.reasons.confirmed-resolution',
          tooltipTranslationKey:
            'operator.custom-bot.trigger.fin-closed-conversation.reasons.confirmed-resolution-tooltip',
          predicate: {
            attribute: 'operator_workflow_trigger.fin_close_reason',
            type: 'string',
            comparison: 'eq',
            value: 'confirmed_resolution',
          },
        },
        {
          icon: 'clock',
          iconSet: 'standard',
          value: 'inactivity',
          translationKey: 'operator.custom-bot.trigger.fin-closed-conversation.reasons.inactivity',
          tooltipTranslationKey:
            'operator.custom-bot.trigger.fin-closed-conversation.reasons.inactivity-tooltip',
          predicate: {
            attribute: 'operator_workflow_trigger.fin_close_reason',
            type: 'string',
            comparison: 'eq',
            value: 'inactivity',
          },
        },
        {
          icon: 'close-conversation',
          iconSet: 'standard',
          value: 'other',
          translationKey: 'operator.custom-bot.trigger.fin-closed-conversation.reasons.other',
          tooltipTranslationKey:
            'operator.custom-bot.trigger.fin-closed-conversation.reasons.other-tooltip',
          predicate: {
            attribute: 'operator_workflow_trigger.fin_close_reason',
            type: 'string',
            comparison: 'eq',
            value: 'other',
          },
        },
      ],
    };
  }

  get columnKeys(): (keyof ColumnConfigs)[] {
    let baseColumns: (keyof ColumnConfigs)[] = super.columnKeys;
    let idx = 2;

    if (getApp().canUseFeature('team-workflows-workflow-type-column')) {
      idx++;
    }

    return [...baseColumns.slice(0, idx), 'channels', ...baseColumns.slice(idx)];
  }

  get requiredBillingFeature() {
    return 'workflows_core_billing_feature';
  }

  get triggerContextType() {
    return DURING_CONVERSATION;
  }

  get workflowsSetupTriggerType(): string[] {
    return [WORKFLOWS_SETUP_TRIGGER_DURING_CONVERSATION, WORKFLOWS_SETUP_TRIGGER_INTERNAL_PROCESS];
  }

  visibleToApp(app: any) {
    return app.canUseFeature('fin-can-trigger-workflow-on-close');
  }
}
